<template>
  <el-form
    ref="formRef"
    :model="formData"
    @input="updateHandler(formRef)"
    @change="updateHandler(formRef)"
  >
    <el-form-item
      :label="translateText('hubstr.editor.modal.field_name')"
      prop="title"
      :rules="[{ required: true, message: translateText('hubstr.editor.errors.required_field'), trigger: 'change' },
               { validator: uniqueValidator, message: 'Поле с таким именем уже было создано', trigger: 'change' }]"
    >
      <el-input
        v-model="titleProxy"
        :maxlength="40"
      />
    </el-form-item>
    <el-form-item label="Тип поля">
      <el-radio-group
        v-model="formData.additions.variableType"
        :disabled="isEdit && !formData.isNew"
      >
        <el-radio label="text">{{ translateText('hubstr.editor.modal.data_type.text') }}</el-radio>
        <el-radio label="link">{{ translateText('hubstr.editor.modal.data_type.link') }}</el-radio>
        <el-radio label="datetime">{{ translateText('hubstr.editor.modal.data_type.date_time') }}</el-radio>
      </el-radio-group>
    </el-form-item>
    <el-form-item>
      <el-checkbox
        v-if="formData.additions.variableType === 'text' || formData.additions.variableType === 'array'"
        v-model="formData.additions.isMultiline"
        :disabled="formData.hasAutoComplete"
        :label="translateText('hubstr.editor.modal.long_text')"
      />
    </el-form-item>
    <el-form-item>
      <el-checkbox v-model="formData.additions.isUserRequired" :label="translateText('hubstr.editor.fields.required_app')" />
    </el-form-item>
    <el-form-item>
      <el-checkbox v-model="formData.additions.isAdminRequired" :label="translateText('hubstr.editor.fields.required_admin')" />
    </el-form-item>
    <el-form-item>
      <el-checkbox v-model="formData.hasPrivacy" :label="translateText('hubstr.editor.fields.has_privacy')" />
    </el-form-item>
    <el-form-item>
      <el-checkbox
        v-if="formData.additions.variableType === 'text' || formData.additions.variableType === 'array'"
        v-model="formData.hasAutoComplete"
        :label="translateText('hubstr.editor.modal.add_autocomplete')"
        :disabled="formData.additions.isMultiline"
      />
    </el-form-item>
    <form-autocomplete
      v-if="formData.hasAutoComplete"
      :field-code="formData.additions.code"
      @update:autocomplete="autocompleteData = $event"
    />
  </el-form>
</template>

<script setup lang="ts">
import {
  reactive, watch, ref, onMounted,
} from 'vue';
import translit from 'features/settings/editor/utils/translit';
import { v4 as uuidV4 } from 'uuid';
import { WIDGET_TYPES } from 'features/settings/editor/association-editor/ui/components/left-side/components/widgets/types/constants';
import { ITextSectionWidget } from 'features/settings/editor/association-editor/ui/components/left-side/components/widgets/types/interfaces';
import { TranslatesAssociationList as t } from 'features/settings/editor/association-editor/requests';
import { FormInstance } from 'element-plus';
import { translate, useTranslate } from 'magner';
import useEditorStore from 'features/settings/editor/store/useEditorStore';
import FormAutocomplete
  from 'features/settings/editor/association-editor/ui/components/left-side/modals/components/form-autocomplete.vue';
import { MOBILE_VALIDATORS } from 'features/settings/mobile-editor/constants';

const { customT } = useTranslate();
const translateText = (code: string) => customT(translate(code));

interface Props {
  data?: ITextSectionWidget;
  isEdit?: boolean;
  isNew?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(['update:formData', 'input:error', 'update:autocomplete']);
const store = useEditorStore();

const state = (props.data && JSON.parse(JSON.stringify(props.data))) as ITextSectionWidget || undefined;
const copy = (props.data && JSON.parse(JSON.stringify(props.data))) as ITextSectionWidget || undefined;

const formRef = ref<FormInstance>();
const autocompleteData = ref();
const formData = reactive<ITextSectionWidget>(
  state
  || {
    id: uuidV4(),
    type: WIDGET_TYPES.TEXT_SECTION,
    title: '',
    name: null,
    text: '',
    dataProvider: null,
    hasAutoComplete: false,
    hasPrivacy: false,
    additions: {
      code: '',
      variableType: 'text',
      allowSearch: false,
      isUserRequired: false,
      isAdminRequired: false,
      glType: 'association',
      entityType: 'association',
      isAutocompletedField: false,
      format: 'DEFAULT',
      hasDisplayType: false,
      formCode: '',
      description: null,
      isMultiline: false,
      mobileValidator: {},
    },
  } as ITextSectionWidget,
);

const titleProxy = ref(t[formData.title?.replace(/lang\.mobile(_ml)?\.?/g, '')] || formData.title);

const formDataBuilder = () => {
  if (!props.isEdit || formData.isNew) {
    // "code" меняется только при создании нового поля(или редактировании нового(!) поля)
    const code = `${formData.additions.entityType}${translit(formData.title, 'pascal')}`;
    formData.additions.code = code;
    formData.additions.formCode = `data.additions.${code}`;
    formData.text = `data.additions.${code}`;
  }
  // если есть перевод оставляем код, если нет то свое значение
  if (titleProxy.value?.toLowerCase() !== t[formData.additions.code]?.toLowerCase()) {
    formData.title = titleProxy.value.trim();
    formData.additions.description = titleProxy.value.trim();
  } else {
    formData.title = `lang.${formData.additions.code}`;
    formData.additions.description = null;
  }

  if (formData.additions.variableType === 'link') {
    formData.name = 'link';
  } else {
    formData.name = null;
  }

  if (formData.additions.variableType !== 'text') {
    formData.hasAutoComplete = false;
    formData.additions.isMultiline = false;
  }

  if (formData.hasAutoComplete) {
    formData.additions.isMultiline = false;
    formData.additions.isAutocompletedField = true;
    formData.additions.allowSearch = true;
    formData.additions.mobileValidator = MOBILE_VALIDATORS.AUTOCOMPLETE;
  } else {
    formData.additions.allowSearch = false;
    formData.additions.isAutocompletedField = false;
    formData.additions.mobileValidator = MOBILE_VALIDATORS.TEXT;
  }

  if (props.isNew) {
    formData.isNew = true; // переменная определяет новое ли поле
  }
  formData.touch = true; // переменная определяет изменилась ли formData
};

const updateHandler = (formEl: FormInstance | undefined) => {
  if (!formEl) return;

  formDataBuilder();

  formEl.validate((valid) => {
    if (valid) {
      // submit

      const out = JSON.parse(JSON.stringify(formData));
      if (out.hasAutoComplete) {
        // особенность бекэнда
        out.additions.variableType = 'array';
      }

      emit('input:error', false);
      emit('update:formData', out);
    } else {
      emit('input:error', true);
    }
  });
};

const uniqueValidator = (rule: any, value: string, callback: any) => {
  if (copy?.additions.formCode !== formData?.additions.formCode && store.associationAdditionalCodes.value.includes(formData.additions.formCode)) {
    callback(new Error());
  } else {
    callback();
  }
};

watch(
  () => formData,
  () => {
    updateHandler(formRef.value);
  },
  { deep: true },
);

/**
 * Принудительно с большой буквы
 * */
watch(() => titleProxy.value, (newValue) => {
  titleProxy.value = newValue.charAt(0).toUpperCase() + newValue.slice(1);
});

watch(() => autocompleteData.value, () => {
  emit('update:autocomplete', { field: formData.additions.code, ...autocompleteData.value });
  emit('input:error', false);
}, { deep: true });

onMounted(() => {
  // особенность бекэнда
  if (formData.additions.variableType === 'array') {
    formData.additions.variableType = 'text';
  }
});
</script>

<style scoped>
.el-checkbox {
  font-weight: lighter;
}
</style>
